/**
 * resume-builder.js — the studio controller.
 *
 * Owns the ResumeModel and the page; every other module is a library.
 *   rail (sections + tools) → ONE pane in the editor column (RbEditor / RbTools)
 *   → the page on the desk (RbPreview, or the letter while the Letter pane is open).
 *
 * Work is kept by the site's draft controller (__toolDraft.editor, «Мои резюме»):
 * every change is saved, a crash or F5 brings the resume back, two tabs never
 * overwrite each other, «New» keeps the current one in the list. The tool used
 * to open its own IndexedDB and save on `beforeunload` (audit 2026-10-09).
 *
 * globalThis.ResumeBuilder.init()
 */
(function () {
    'use strict';

    var T = function (k, v) { return globalThis.RbI18n.t(k, v); };
    var $ = function (id) { return document.getElementById(id); };
    var S, cfg, ed = null;
    var state = null, pane = 'personal', view = 'start', commitTimer = null, lastChecks = null;
    var CONTENT = ['personal', 'summary', 'experience', 'education', 'skills', 'languages'];

    /* ---- telemetry ----------------------------------------------------- */
    function track(f, extra) {
        try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: 'resume-builder', f: f }, extra || {})); } catch (e) {}
    }
    function stage(s) { try { if (window.__stage) window.__stage(s); } catch (e) {} }
    function toast(msg, type) { if (window.__toast) window.__toast(msg, type || 'info'); }

    /* ---- model helpers ------------------------------------------------- */
    function defaultRegion() { return globalThis.RbConventions.defaultRegion(globalThis.RbI18n.LOCALE); }
    function fresh(region) {
        region = region || defaultRegion();
        var m = S.createFreshState(region, globalThis.RbI18n.LOCALE);
        m.cultural = globalThis.RbConventions.culturalDefaults(region);
        m.meta.pageSize = globalThis.RbConventions.pageSize(region);
        m.meta.template = globalThis.RbConventions.recommendedTemplates(region)[0] || 'classic';
        return m;
    }
    function adopt(raw) {
        var region = (raw && raw.meta && raw.meta.region) || defaultRegion();
        var m = S.migrate(raw, region);
        if (!raw || !raw.meta || !raw.meta.region) {
            m.cultural = Object.assign(globalThis.RbConventions.culturalDefaults(region), raw && raw.cultural || {});
            m.meta.pageSize = globalThis.RbConventions.pageSize(region);
            m.meta.template = (raw && raw.meta && raw.meta.template) || globalThis.RbConventions.recommendedTemplates(region)[0] || 'classic';
        }
        if (!m.meta.lang) m.meta.lang = globalThis.RbI18n.LOCALE;
        if (raw && raw.personal && raw.personal.photo) m.cultural.photo = m.cultural.photo || !!raw.personal.photo;
        return m;
    }
    function dateFmt() { return globalThis.RbConventions.dateFormat(state.meta.region); }
    function isRtl() { return globalThis.RbI18n.docLang(state) === 'ar'; }
    function renderOpts() { return { dateFormat: dateFmt(), rtl: isRtl() }; }

    /* What the assistant gets: the resume WITHOUT photo and contacts (the server
       drops them too — this keeps them off the wire and the request small). */
    function payloadModel() {
        var c = S.clone(state);
        c.personal = { title: state.personal.title || '' };
        delete c.qr; delete c.letter; delete c.target; delete c.cultural;
        return c;
    }

    /* ---- the commit cycle ---------------------------------------------- */
    function changed(scope) {
        if (scope !== 'meta' && ed) ed.touch(scope !== 'live');
        try { localStorage.setItem('rb-open-work', S.isEmpty(state) ? '0' : '1'); } catch (e) {}
        clearTimeout(commitTimer);
        commitTimer = setTimeout(function () { refresh(scope); }, 140);
    }
    function structure() { changed('structure'); paintNav(); }

    var _qrSig = '';
    function syncQr() {
        var q = state.qr || (state.qr = { url: '', include: false, dataUrl: '' });
        var sig = (q.include && q.url) ? q.url : '';
        if (sig === _qrSig) return;
        _qrSig = sig;
        if (!sig) { q.dataUrl = ''; return; }
        globalThis.RbQr.toDataUrl(sig).then(function (du) { if (_qrSig === sig && state.qr) { state.qr.dataUrl = du; paintStage(); } }, function () {});
    }

    function refresh(scope) {
        syncQr();
        paintStage();
        paintNav();
        paintMeter();
        if (pane === 'check' && scope !== 'design') globalThis.RbTools.show('check', true);
        if (scope === 'design' && pane === 'skills') globalThis.RbEditor.rerender();
    }

    function paintStage() {
        var name = T((globalThis.RbTemplates.get(state.meta.template) || {}).nameKey || 'tpl_classic');
        $('rbTplName').textContent = name;
        if (pane === 'letter' && String((state.letter || {}).body || '').trim()) {
            var paper = $('rbDoc');
            paper.innerHTML = globalThis.RbCoverLetter.letterHtml(state, { rtl: isRtl(), marks: true });
            var doc = paper.firstElementChild;
            var z = globalThis.RbPreview.zoom();
            var pg = S.pageSize(state.meta.pageSize);
            doc.style.transform = 'scale(' + z + ')';
            paper.style.width = Math.round(pg.w * z) + 'px';
            paper.style.height = Math.round(Math.max(pg.h, doc.offsetHeight) * z) + 'px';
            $('rbPageInfo').textContent = T('cl_short');
            return;
        }
        globalThis.RbPreview.repaint(state, renderOpts());
    }

    function checks() {
        lastChecks = globalThis.RbChecks.run(state, {
            lang: globalThis.RbI18n.docLang(state),
            conventions: globalThis.RbConventions.get(state.meta.region),
            pages: globalThis.RbPreview.pageCount()
        });
        return lastChecks;
    }
    function paintMeter() {
        var r = checks();
        var arc = $('rbMeterArc');
        $('rbMeterVal').textContent = r.score;
        if (arc) {
            arc.setAttribute('stroke-dashoffset', String(94.25 * (1 - r.score / 100)));
            arc.setAttribute('stroke', r.band === 'ready' ? 'var(--lg-success)' : r.band === 'almost' ? 'var(--lg-warn)' : 'var(--lg-danger)');
        }
        $('rbMeter').title = r.failed ? T('chk_left', { n: r.failed }) : T('chk_all_ok');
    }

    /* ---- rail ---------------------------------------------------------- */
    function navOrder() {
        var order = (state.meta.sectionOrder || []).filter(function (s) { return s !== 'personal'; });
        var shown = ['personal'];
        order.forEach(function (s) {
            if (CONTENT.indexOf(s) > -1 || (state[s] || []).length || extraShown.indexOf(s) > -1) shown.push(s);
        });
        (state.custom || []).forEach(function (c, i) { shown.push('custom:' + i); });
        return shown;
    }
    var extraShown = [];
    function paneLabel(id) {
        if (id === 'personal') return T('nav_personal');
        if (/^custom:/.test(id)) return (state.custom[+id.split(':')[1]] || {}).title || T('section_custom');
        if (globalThis.RbTools.has(id)) return T('nav_' + id);
        return T('section_' + id);
    }
    function paintNav() {
        var ul = $('rbNavContent');
        var order = navOrder();
        ul.innerHTML = order.map(function (id) {
            var st = /^custom:/.test(id) ? ((state.custom[+id.split(':')[1]].items || []).some(function (x) { return String(x).trim(); }) ? 'done' : 'empty') : globalThis.RbChecks.sectionState(state, id);
            var cnt = Array.isArray(state[id]) && state[id].length ? state[id].length : '';
            return '<li><button type="button" class="rbx-nav-b" data-pane="' + id + '" data-st="' + st + '"' + (id === pane ? ' aria-current="page"' : '') + '>' +
                '<span class="rbx-dot" aria-hidden="true"></span><span class="rbx-nav-l">' + globalThis.RbAi.esc(paneLabel(id)) + '</span>' +
                '<span class="rbx-count">' + cnt + '</span><span class="tc-sr">' + globalThis.RbAi.esc(T('state_' + st)) + '</span></button></li>';
        }).join('');
        document.querySelectorAll('#rbNavTools [data-pane]').forEach(function (b) {
            if (b.dataset.pane === pane) b.setAttribute('aria-current', 'page'); else b.removeAttribute('aria-current');
        });
        var hh = document.querySelector('[data-region-only="ru"]');
        if (hh) hh.hidden = state.meta.region !== 'ru';
        var cur = $('rbNavCurL');
        if (cur) cur.textContent = paneLabel(pane);
        var all = fullOrder(), at = all.indexOf(pane);
        $('rbNavPrev').disabled = at <= 0;
        $('rbNavNext').disabled = at < 0 || at >= all.length - 1;
        /* optional sections already on the page leave the "add" menu */
        document.querySelectorAll('#rbAddMenu [data-add-sec]').forEach(function (b) {
            var s = b.dataset.addSec;
            b.hidden = s !== 'custom' && order.indexOf(s) > -1;
        });
    }

    /* everything the phone's ‹ › walk through: the sections, then the tools */
    function fullOrder() {
        var tools = ['design', 'check', 'job', 'letter'];
        if (state.meta.region === 'ru') tools.push('hh');
        return navOrder().concat(tools);
    }
    function navOpen(open) {
        var nav = $('rbNav');
        nav.classList.toggle('open', !!open);
        $('rbNavCur').setAttribute('aria-expanded', String(!!open));
    }

    function go(id, o) {
        o = o || {};
        navOpen(false);
        if (view !== 'edit') setView('edit');
        pane = id;
        var ok = globalThis.RbTools.has(id) ? globalThis.RbTools.show(id) : globalThis.RbEditor.show(id);
        if (!ok) { pane = 'personal'; globalThis.RbEditor.show('personal'); }
        paintNav();
        paintStage();
        if (o.focus !== false) {
            var pn = $('rbPane');
            var heading = pn.querySelector('.rbx-pane-t');
            if (heading) { heading.setAttribute('tabindex', '-1'); try { heading.focus({ preventScroll: true }); } catch (e) {} }
        }
        if (window.innerWidth < 760 && $('rbApp').dataset.mob === 'preview') setMob('edit');
        track('pane', { pane: /^custom:/.test(id) ? 'custom' : id });
    }

    function focusField(paneId, o) {
        if (globalThis.RbTools.has(paneId)) { go(paneId); return; }
        pane = paneId;
        if (view !== 'edit') setView('edit');
        globalThis.RbEditor.focusField(paneId, o || {});
        paintNav();
        paintStage();
    }

    /* ---- views --------------------------------------------------------- */
    function setView(v) {
        view = v;
        $('rbApp').dataset.view = v;
        $('rbApp').removeAttribute('data-boot');
        if (v === 'edit') stage('edit');
    }
    function setMob(mode) {
        $('rbApp').dataset.mob = mode;
        document.querySelectorAll('[data-mob-tab]').forEach(function (b) {
            var on = b.dataset.mobTab === mode;
            b.classList.toggle('on', on);
            b.setAttribute('aria-selected', String(on));
        });
        if (mode === 'preview') requestAnimationFrame(function () { globalThis.RbPreview.fit(); paintStage(); });
    }

    /* Load a model as the current work. `newWork` makes it a NEW resume in
       «Мои резюме» (the current one stays in the list) — used by start actions,
       import and translation, so nothing the person had is overwritten. */
    function load(model, o) {
        o = o || {};
        state = model ? adopt(model) : fresh();
        extraShown = [];
        globalThis.RbEditor.setModel(state);
        globalThis.RbTools.resetAnswers();
        _qrSig = '';
        setView('edit');
        go(o.pane || 'personal', { focus: o.focus });
        globalThis.RbPreview.fit();
        refresh('structure');
        if (o.notice) notice(o.notice);
    }
    var _afterFresh = null;
    function newWork(model, o) {
        o = o || {};
        var doLoad = function () {
            load(model, o);
            if (ed) ed.touch(true);
            track('start', { why: o.why || '', sample: o.sample || '' });
        };
        /* «New» in the draft controller saves the current resume first, then calls
           our fresh() — asynchronously and inside its quiet window, where changes do
           not count. So the new work is loaded right AFTER fresh(), outside it. */
        if (ed && ed.store && ed.store() && state && view === 'edit' && !S.isEmpty(state)) {
            _afterFresh = doLoad;
            ed.reset();
        } else doLoad();
    }

    /* ---- notices / busy (import) -------------------------------------- */
    var _notice = null;
    function notice(text, link) {
        if (_notice) _notice.remove();
        var host = view === 'start' ? $('rbStart') : $('rbPane');
        _notice = document.createElement('div');
        _notice.className = 'rbx-sugg';
        _notice.setAttribute('role', 'status');
        _notice.innerHTML = '<p class="rbx-sugg-text">' + globalThis.RbAi.esc(text) + '</p>' +
            (link ? '<div class="rbx-sugg-act"><a class="tc-btn ghost sm" href="' + globalThis.RbAi.esc(link.href) + '">' + globalThis.RbAi.esc(link.label) + '</a></div>' : '');
        host.insertBefore(_notice, host.firstChild);
    }
    var _busy = null;
    function busy(text, withBar) {
        if (_busy) { _busy.remove(); _busy = null; }
        if (!text) return;
        _busy = globalThis.RbAi.waitBlock($('rbStart').parentNode === $('rbEditor') && view === 'start' ? $('rbStart') : $('rbPane'), text);
        _busy.stopBtn.hidden = true;
        if (!withBar) _busy.el.querySelector('.rbx-wait-bar').hidden = true;
        _busy.el.parentNode.insertBefore(_busy.el, _busy.el.parentNode.firstChild);
    }
    function busyProgress(w) { if (_busy) _busy.set(w); }

    /* ---- confirm (native <dialog>) ------------------------------------- */
    function confirmDlg(title, text, yes, onYes) {
        var dlg = $('rbConfirmDlg');
        if (!dlg || !dlg.showModal) { if (window.confirm(title + '\n' + (text || ''))) onYes(); return; }
        $('rbConfirmH').textContent = title;
        $('rbConfirmText').textContent = text || '';
        $('rbConfirmYes').textContent = yes || 'OK';
        dlg.returnValue = '';
        dlg.onclose = function () { if (dlg.returnValue === 'ok') onYes(); };
        dlg.showModal();
    }

    /* ---- export -------------------------------------------------------- */
    function guardExport(kind) {
        if (!String(state.personal.name || '').trim()) {
            toast(T('export_need_name'), 'warning');
            track('export_blocked', { reason: 'no_name', kind: kind });
            focusField('personal', { field: 'name' });
            return true;
        }
        return false;
    }
    function exportError(fmt) {
        return function (e) {
            track('export_failed', { format: fmt });
            if (window.__handleError) window.__handleError(e, 'resume_export_' + fmt, { say: T('export_failed') });
        };
    }
    function doExport(fmt) {
        if (fmt !== 'json' && guardExport('resume')) return;
        var go2 = function () { runExport(fmt); };
        var ph = S.placeholders(state);
        if (fmt !== 'json' && ph.length) {
            confirmDlg(T('export_ph_q', { n: ph.length }), T('export_ph_d', { first: ph[0] }), T('export_anyway'), go2);
            return;
        }
        if (fmt !== 'json' && state.meta.sample) {
            confirmDlg(T('export_sample_q'), T('export_sample_d'), T('export_anyway'), go2);
            return;
        }
        go2();
    }
    function runExport(fmt) {
        var o = renderOpts();
        var p;
        track('export', { format: fmt, template: state.meta.template, region: state.meta.region, score: lastChecks ? lastChecks.score : null });
        stage('export');
        if (fmt === 'pdf') { toast(T('export_opening_print')); p = globalThis.RbPrint.printResume(state, { dateFormat: o.dateFormat, rtl: o.rtl, locale: globalThis.RbI18n.docLang(state) }).then(function () { return true; }); }
        else if (fmt === 'ats-pdf') { toast(T('exporting')); p = globalThis.RbPdf.exportPdf(state, { rtl: o.rtl, dateFormat: o.dateFormat }); }
        else if (fmt === 'docx') { toast(T('exporting')); p = globalThis.RbDocx.exportDocx(state, { dateFormat: o.dateFormat }); }
        else if (fmt === 'txt') p = globalThis.RbExportText.exportTxt(state);
        else if (fmt === 'json') p = globalThis.RbExportText.exportJson(state);
        else if (fmt === 'png') p = exportPng();
        Promise.resolve(p).then(function (ok) {
            if (ok === false) return;
            if (fmt !== 'pdf') toast(T('exported'), 'success');
            if (ed && fmt !== 'json') ed.exported();
            track('export_done', { format: fmt });
            try { if (window.__aeToolComplete) window.__aeToolComplete({ format: fmt, template: state.meta.template, pages: globalThis.RbPreview.pageCount() }); } catch (e) {}
            stage('complete');
        }, exportError(fmt));
    }
    function exportPng() {
        return globalThis.RbExportText.loadVendor('html2canvas').then(function () {
            var host = document.createElement('div');
            host.style.cssText = 'position:absolute;left:-10000px;top:0;width:' + S.pageSize(state.meta.pageSize).w + 'px';
            host.innerHTML = globalThis.RbRender.render(state, Object.assign({ target: 'png' }, renderOpts()));
            document.body.appendChild(host);
            var node = host.firstElementChild;
            return window.html2canvas(node, { scale: 2, backgroundColor: '#fff', useCORS: true }).then(function (canvas) {
                host.remove();
                var toBlob = window.__canvasBlob ? window.__canvasBlob(canvas, 'image/png') : new Promise(function (r) { canvas.toBlob(r, 'image/png'); });
                return toBlob;
            }, function (e) { host.remove(); throw e; });
        }).then(function (blob) {
            return globalThis.RbExportText.download(blob, globalThis.RbExportText.safeName(state, 'png'));
        });
    }

    /* ---- translation (into a COPY) ------------------------------------- */
    function translateItems() {
        var items = [];
        var add = function (id, v) { if (String(v || '').trim()) items.push({ id: id, text: String(v) }); };
        add('title', state.personal.title);
        add('summary', state.summary);
        ['experience', 'education', 'projects', 'volunteer', 'certifications', 'awards'].forEach(function (sec) {
            (state[sec] || []).forEach(function (e, i) {
                ['position', 'role', 'degree', 'field', 'honors', 'description', 'achievements', 'name', 'title', 'location'].forEach(function (f) {
                    if (e[f] !== undefined) add(sec + '.' + i + '.' + f, e[f]);
                });
            });
        });
        (state.skills || []).forEach(function (k, i) { add('skills.' + i + '.name', k.name); });
        (state.languages || []).forEach(function (k, i) { add('languages.' + i + '.name', k.name); });
        S.PREF_KEYS.forEach(function (k) { add('prefs.' + k, (state.prefs || {})[k]); });
        (state.custom || []).forEach(function (c, i) { add('custom.' + i + '.title', c.title); (c.items || []).forEach(function (x, j) { add('custom.' + i + '.items.' + j, x); }); });
        return items;
    }
    function openTranslate() {
        var dlg = $('rbTrDlg');
        $('rbTrStatus').textContent = '';
        var d = dlg.querySelector('[data-ai-disclose]');
        if (d) d.innerHTML = globalThis.RbAi.disclosure();
        dlg.showModal();
        track('translate_open');
    }
    function runTranslate() {
        var to = $('rbTrTo').value;
        var from = globalThis.RbI18n.docLang(state);
        var st = $('rbTrStatus'), btn = $('rbTrGo');
        if (to === from) { st.textContent = T('translate_same'); return; }
        var items = translateItems();
        if (!items.length) { st.textContent = T('ai_need_resume'); return; }
        btn.disabled = true;
        st.textContent = T('translate_running');
        globalThis.RbAi.run('translate', { from: from, to: to, items: items.slice(0, 120) }, {
            onWait: function (w) { st.textContent = T('translate_running') + ' ' + (w.waited < w.eta ? T('ai_wait_usual', { s: Math.round(w.eta) }) : T('ai_wait_long')); }
        }).then(function (res) {
            btn.disabled = false;
            var copy = S.clone(state);
            (res.items || []).forEach(function (it) {
                var path = it.id.split('.');
                var obj = copy;
                if (path[0] === 'title') { copy.personal.title = it.text; return; }
                if (path[0] === 'summary') { copy.summary = it.text; return; }
                if (path[0] === 'prefs') { copy.prefs[path[1]] = it.text; return; }
                if (path[0] === 'custom' && path[2] === 'items') { copy.custom[+path[1]].items[+path[3]] = it.text; return; }
                for (var i = 0; i < path.length - 1; i++) obj = obj[isNaN(+path[i]) ? path[i] : +path[i]];
                if (obj) obj[path[path.length - 1]] = it.text;
            });
            copy.meta.lang = to;
            copy.meta.sample = '';
            $('rbTrDlg').close();
            newWork(copy, { pane: pane === 'letter' ? 'personal' : pane, why: 'translate', notice: T('translate_done_copy', { lang: $('rbTrTo').selectedOptions[0].textContent }) });
            track('translate_done', { from: from, to: to, untranslated: res.untranslated || 0 });
        }, function (e) {
            btn.disabled = false;
            // Перевод за токены: предложение с ценой рядом со строкой статуса.
            var pay = document.getElementById('rbTrPay');
            if (pay && e && e.data && globalThis.__lanePay
                && globalThis.__lanePay.show(pay, e.data, { slug: 'resume-builder', run: function () { btn.click(); } })) {
                st.textContent = '';
                return;
            }
            st.textContent = (e && e.message) || T('translate_failed');
        });
    }

    /* ---- region -------------------------------------------------------- */
    function applyRegion(region) {
        state.meta.region = region;
        state.cultural = globalThis.RbConventions.culturalDefaults(region);
        state.meta.pageSize = globalThis.RbConventions.pageSize(region);
        track('region', { region: region });
        changed('design');
        paintNav();
    }

    /* ---- binding ------------------------------------------------------- */
    function bind() {
        $('rbNav').addEventListener('click', function (e) {
            var b = e.target.closest('[data-pane]');
            if (b) go(b.dataset.pane);
        });
        $('rbNavCur').addEventListener('click', function () { navOpen(!$('rbNav').classList.contains('open')); });
        $('rbNavPrev').addEventListener('click', function () { var a = fullOrder(), i = a.indexOf(pane); if (i > 0) go(a[i - 1]); });
        $('rbNavNext').addEventListener('click', function () { var a = fullOrder(), i = a.indexOf(pane); if (i > -1 && i < a.length - 1) go(a[i + 1]); });
        document.querySelectorAll('#rbAddMenu [data-add-sec]').forEach(function (b) {
            b.addEventListener('click', function () {
                var s = b.dataset.addSec;
                try { $('rbAddMenu').hidePopover(); } catch (e) {}
                if (s === 'custom') {
                    state.custom.push({ title: '', items: [''] });
                    structure();
                    go('custom:' + (state.custom.length - 1));
                    return;
                }
                if (extraShown.indexOf(s) < 0) extraShown.push(s);
                if (!(state[s] || []).length) state[s].push(S.newEntry(s));
                structure();
                go(s);
            });
        });
        document.querySelectorAll('[data-mob-tab]').forEach(function (b) {
            b.addEventListener('click', function () { setMob(b.dataset.mobTab); });
        });
        $('rbMeter').addEventListener('click', function () { go('check'); });
        $('rbTplBtn').addEventListener('click', function () { go('design'); });
        $('rbZoomIn').addEventListener('click', function () { globalThis.RbPreview.zoomIn(); paintStage(); });
        $('rbZoomOut').addEventListener('click', function () { globalThis.RbPreview.zoomOut(); paintStage(); });
        $('rbFit').addEventListener('click', function () { globalThis.RbPreview.fit(); paintStage(); });
        document.querySelectorAll('#rbDlMenu [data-fmt]').forEach(function (b) {
            b.addEventListener('click', function () {
                try { $('rbDlMenu').hidePopover(); } catch (e) {}
                if (view !== 'edit') { toast(T('export_nothing'), 'warning'); return; }
                doExport(b.dataset.fmt);
            });
        });
        var tr = document.querySelector('#rbDlMenu [data-act="translate"]');
        if (tr) {
            if (!globalThis.RbAi.live()) tr.hidden = true;
            tr.addEventListener('click', function () {
                try { $('rbDlMenu').hidePopover(); } catch (e) {}
                if (view !== 'edit' || S.isEmpty(state)) { toast(T('ai_need_resume'), 'warning'); return; }
                openTranslate();
            });
        }
        $('rbTrGo').addEventListener('click', runTranslate);
        $('rbContinueBtn').addEventListener('click', function () {
            var lb = $('rbDraftsListBtn');
            if (lb) lb.click();
        });
        document.querySelectorAll('[data-early-action]').forEach(function (b) {
            b.addEventListener('click', function () { globalThis.RbIntake.act(b.dataset.earlyAction); });
        });
    }

    /* ---- drafts («Мои резюме») ----------------------------------------- */
    function bootDrafts() {
        if (!window.__toolDraft || !window.__toolDraft.editor) return Promise.resolve(false);
        var restored = false;
        ed = window.__toolDraft.editor({
            slug: 'resume-builder',
            root: 'rbDrafts',
            t: function (k, f) { var v = T(k); return v === k ? f : v; },
            track: function (a, d) { track('draft_' + a, d); },
            toast: toast,
            payload: function () {
                if (!state || S.isEmpty(state)) return null;
                var label = [state.personal.name, state.personal.title].filter(function (x) { return String(x || '').trim(); }).join(' — ') || T('drafts_untitled');
                return { state: S.clone(state), label: label.slice(0, 80), meta: { template: state.meta.template, lang: state.meta.lang } };
            },
            apply: function (d) {
                restored = true;
                load(d && d.state ? d.state : null, { pane: 'personal', focus: false });
                return Promise.resolve([]);
            },
            fresh: function () {
                state = fresh();
                globalThis.RbEditor.setModel(state);
                var after = _afterFresh;
                _afterFresh = null;
                if (after) { setTimeout(after, 0); return; }
                setView('start');
                paintStage();
                showSamplePreview();
                try { localStorage.setItem('rb-open-work', '0'); } catch (e) {}
            },
            started: function () { return view === 'edit' && state && !S.isEmpty(state); }
        });
        return Promise.resolve(ed.boot()).then(function () { return restored; }, function () { return restored; });
    }

    /* On the start screen the desk shows an example in the recommended template —
       what the result looks like, before anything is typed. */
    function showSamplePreview() {
        var data = globalThis.__rbSamples || {};
        var s = (data.professions || [])[0] || data.first;
        if (!s) { paintStage(); return; }
        var keep = state;
        var m = adopt(s.model);
        m.meta.template = keep.meta.template;
        m.meta.sample = s.id;
        var prev = state;
        state = m;
        globalThis.RbPreview.repaint(state, renderOpts());
        state = prev;
    }

    function showContinue() {
        if (!ed || !ed.store) return;
        var st = ed.store();
        if (!st || !st.list) return;
        Promise.resolve(st.list()).then(function (list) {
            list = (list || []).filter(function (d) { return d && d.label; });
            if (!list.length || view !== 'start') return;
            $('rbContinueText').textContent = T('start_continue_t', { label: list[0].label, n: list.length });
            $('rbContinue').hidden = false;
        }, function () {});
    }

    /* ---- init ---------------------------------------------------------- */
    function init() {
        S = globalThis.RbState;
        cfg = globalThis.__rbCfg || {};
        globalThis.RbI18n.setStrings(globalThis.__rbStrings || {});
        globalThis.RbConventions.setProfiles(globalThis.__rbConventions || {});
        state = fresh();

        globalThis.RbAi.init({ cfg: cfg, t: T, track: track, chip: $('rbAiChip') });
        globalThis.RbPreview.mount({
            paperEl: $('rbDoc'), deskEl: $('rbDesk'), pageInfoEl: $('rbPageInfo'), zoomOutEl: $('rbZoomVal'),
            labels: { page: T('page_n'), pages: T('pages_label'), sample: T('sample_tag') }
        });
        globalThis.RbEditor.mount($('rbPane'), state, {
            onChange: function (s) { changed(s); },
            onStructure: structure,
            go: go,
            order: function () { return navOrder(); },
            label: paneLabel,
            confirm: confirmDlg,
            track: track,
            payloadModel: payloadModel,
            relabel: paintNav,
            dropSection: function (id) { extraShown = extraShown.filter(function (s) { return s !== id; }); go('personal'); }
        });
        globalThis.RbTools.mount($('rbPane'), {
            model: function () { return state; },
            changed: function (s) { changed(s); },
            structure: structure,
            focus: focusField,
            go: go,
            track: track,
            checks: checks,
            renderOpts: renderOpts,
            payloadModel: payloadModel,
            applyRegion: applyRegion,
            confirm: confirmDlg,
            guardExport: guardExport,
            exportError: exportError,
            rtl: isRtl
        });
        globalThis.RbIntake.init({
            newWork: newWork, track: track, busy: busy, busyProgress: busyProgress, notice: notice,
            model: function () { return state; }, defaultRegion: defaultRegion, cfg: function () { return cfg; }
        });
        globalThis.RbIntake.mountListeners();
        bind();
        globalThis.RbPreview.fit();
        showSamplePreview();
        paintNav();
        stage('start');
        track('open');

        bootDrafts().then(function (restored) {
            if (window.__earlyAction) {
                if (restored) window.__earlyAction.claim('resume-builder');
                else window.__earlyAction.claim('resume-builder', function (a) { globalThis.RbIntake.act(a); });
            }
            if (!restored) {
                $('rbApp').removeAttribute('data-boot');
                showContinue();
            }
        });
    }

    globalThis.ResumeBuilder = { init: init, _state: function () { return state; }, _go: go };

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
})();
